import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { LoaderComponent } from '../../../../components/widgets/loader/loader.component';
import { LoaderConfig } from '../../../../components/widgets/loader/loader.config';
import { MeetingMinuteFormComponent } from '../../components/meeting-minute-form/meeting-minute-form.component';

import { QualityManagementService } from '../../../../services/quality-management.service';
import { RouteService } from '../../../../services/route.service';
import { LoaderService } from '../../../../services/loader.service';
import { AlertNotificationService } from '../../../../services/alert-notification.service';
import { MainLayoutService } from '../../../../services/main-layout.service';
import { AccessControlService } from '../../../../services/access-control.service';

import { MeetingMinute } from '../../../../models/entities/meeting-minute';

@Component({
  selector: 'app-meeting-minutes-detail-page',
  imports: [LoaderComponent, MeetingMinuteFormComponent],
  templateUrl: './meeting-minutes-detail-page.component.html',
  styleUrl: './meeting-minutes-detail-page.component.scss',
})
export class MeetingMinutesDetailPageComponent implements OnInit, OnDestroy {

  meetingMinuteId?: number;
  meetingMinute?: MeetingMinute;

  canModify: boolean = false;
  isLoading: boolean = false;
  loaderConfig: LoaderConfig = {
    type: 'spinner',
    text: 'Loading...',
    overlay: true
  };

  private readonly LOADER_CONTEXT = 'meeting-minutes-detail-page';
  private destroy$ = new Subject<void>();

  constructor(
    private route: ActivatedRoute,
    private qualityManagementService: QualityManagementService,
    private routeService: RouteService,
    private loaderService: LoaderService,
    private alertNotificationService: AlertNotificationService,
    private mainLayoutService: MainLayoutService,
    private accessControlService: AccessControlService
  ) { }

  ngOnInit(): void {
    window.scrollTo({ top: 0 });

    this.mainLayoutService.activeProfile$
      .pipe(takeUntil(this.destroy$))
      .subscribe(profileCode => {
        this.canModify = this.accessControlService.hasFeatureActionAccess(
          profileCode,
          'QUALITY_MANAGEMENT_MEETING_MINUTES',
          'modify'
        );
      });

    this.loaderService.getLoadingAsObservable(this.LOADER_CONTEXT)
      .pipe(takeUntil(this.destroy$))
      .subscribe(isLoading => this.isLoading = isLoading);

    const paramId = this.route.snapshot.paramMap.get('meetingMinuteId');
    if (paramId) {
      this.meetingMinuteId = Number(paramId);
      this.loadMeetingMinute(this.meetingMinuteId);
    }
  }

  ngOnDestroy(): void {
    this.loaderService.stopLoading(this.LOADER_CONTEXT);
    this.destroy$.next();
    this.destroy$.complete();
  }

  goToEdit(): void {
    if (!this.meetingMinuteId) {
      return;
    }
    this.routeService.meetingMinutesUpdate(this.meetingMinuteId);
  }

  private loadMeetingMinute(meetingMinuteId: number): void {
    this.loaderService.startLoading(this.LOADER_CONTEXT);

    this.qualityManagementService.detailMeetingMinute(meetingMinuteId).subscribe({
      next: (response: any) => {
        const meetingMinute: MeetingMinute | null =
          this.qualityManagementService.readResponseKey(response, 'meetingMinute') ?? null;
        this.loaderService.stopLoading(this.LOADER_CONTEXT);

        if (!meetingMinute) {
          this.alertNotificationService.error('Meeting minute not found.');
          return;
        }

        this.meetingMinute = meetingMinute;
      },
      error: (error: any) => {
        console.error('Error loading the meeting minute:', error);
        this.loaderService.stopLoading(this.LOADER_CONTEXT);
        this.alertNotificationService.error(
          this.qualityManagementService.meetingMinuteErrorMessage(error, 'Failed to load the meeting minute.')
        );
      }
    });
  }
}
